import React from 'react'
import { IoClose } from 'react-icons/io5'
import { MobileSearchFilters } from './hooks/useMobileSearch'
import useApiService from '../../services/api'

interface MobileSearchAppliedFiltersProps {
  filters: MobileSearchFilters
  onRemoveFilter: (
    filterKey: 'category' | 'type',
    valueToRemove?: string
  ) => void
}

export const MobileSearchAppliedFilters: React.FC<
  MobileSearchAppliedFiltersProps
> = ({ filters, onRemoveFilter }) => {
  const api = useApiService()
  const { data: categoriesData } = api.useGetActiveCompanyCategoriesAndTypes()

  // Build lookup maps once for O(1) access (optimization for repeated lookups)
  // IMPORTANT: These hooks must always be called before any conditional returns
  const categoryMap = React.useMemo(() => {
    if (!categoriesData) return new Map<string, string>()
    return new Map(categoriesData.map((cat) => [cat.slug, cat.name]))
  }, [categoriesData])

  const typeMap = React.useMemo(() => {
    if (!categoriesData) return new Map<string, string>()
    const map = new Map<string, string>()
    categoriesData.forEach((category) => {
      category.sub_categories.forEach((sub) => {
        map.set(sub.slug, sub.name)
      })
    })
    return map
  }, [categoriesData])

  // Don't render if no filters applied (check AFTER all hooks)
  if (!filters.category && !filters.type) {
    return null
  }

  // Get category data (slug + name) from comma-separated slugs - O(1) lookup
  const getCategoryData = (
    categorySlugs: string | undefined
  ): Array<{ slug: string; name: string }> => {
    if (!categorySlugs) return []
    const slugs = categorySlugs.split(',').filter(Boolean)
    return slugs
      .map((slug) => {
        const trimmedSlug = slug.trim()
        const name = categoryMap.get(trimmedSlug)
        return name ? { slug: trimmedSlug, name } : null
      })
      .filter(Boolean) as Array<{ slug: string; name: string }>
  }

  // Get type data (slug + name) from comma-separated slugs - O(1) lookup
  const getTypeData = (
    typeSlugs: string | undefined
  ): Array<{ slug: string; name: string }> => {
    if (!typeSlugs) return []
    const slugs = typeSlugs.split(',').filter(Boolean)
    return slugs
      .map((slug) => {
        const trimmedSlug = slug.trim()
        const name = typeMap.get(trimmedSlug)
        return name ? { slug: trimmedSlug, name } : null
      })
      .filter(Boolean) as Array<{ slug: string; name: string }>
  }

  const categoryData = getCategoryData(filters.category)
  const typeData = getTypeData(filters.type)

  // Build array of active filters (one badge per selected value)
  const activeFilters: Array<{
    key: 'category' | 'type'
    label: string
    value: string
    slug: string
  }> = []

  categoryData.forEach(({ slug, name }) => {
    activeFilters.push({
      key: 'category',
      label: 'Category:',
      value: name,
      slug,
    })
  })

  typeData.forEach(({ slug, name }) => {
    activeFilters.push({
      key: 'type',
      label: 'Type:',
      value: name,
      slug,
    })
  })

  if (activeFilters.length === 0) {
    return null
  }

  const firstFilter = activeFilters[0]
  const additionalCount = activeFilters.length - 1

  return (
    <div className="p-0 mt-2 flex flex-wrap justify-end gap-2 bg-white">
      <div className="inline-flex items-center gap-1 px-2 py-1 bg-pistachio-200 text-fb-green-900 rounded text-xs font-medium">
        <span className="font-semibold">{firstFilter.label}</span>
        <span className="font-normal">{firstFilter.value}</span>
        <button
          type="button"
          onClick={() => onRemoveFilter(firstFilter.key, firstFilter.slug)}
          aria-label={`Remove ${firstFilter.value} filter`}
          className="bg-transparent border-0 text-fb-green-900 cursor-pointer p-0 flex items-center justify-center w-3.5 h-3.5 ml-0.5 rounded-full transition-colors hover:bg-pistachio-100"
        >
          <IoClose size={14} />
        </button>
      </div>

      {additionalCount > 0 && (
        <div className="inline-flex items-center gap-1 px-2 py-1 bg-pistachio-200 text-fb-green-900 rounded text-xs font-medium">
          <span className="font-normal">+{additionalCount}</span>
        </div>
      )}
    </div>
  )
}
